import { Box, Button, Typography, useTheme } from "@mui/material";
import { GridRowParams } from "@mui/x-data-grid";
import { ArrowRightIcon } from "@mui/x-date-pickers";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { WasteStreamExportDropdown } from "./WasteStreamExportDropdown";
import { gql } from "generated-graphql";
import {
  WasteClassification,
  WasteStreamsQuery,
  WasteStreamsQueryVariables,
} from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useOmnisearchInput } from "hooks/useOmnisearchDatagrid/useOmnisearchInput";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useTenant } from "hooks/useTenant";
import { useMemo, useState } from "react";
import { formatAmountInPounds } from "util/formatNumber";
import { prettyPrintWasteClassification } from "../../prettyPrintWasteClassification";
import { useWasteFacility } from "../../useWasteFacility";
import { MergedWasteStreamChip } from "../MergedWasteStreamChip";
import { useWasteFacilityState } from "../WasteFacilityContext";
import { WasteStreamDetailsModal } from "../WasteStreamDetails/WasteStreamDetailsModal";
import { useWasteStreamSelection } from "./useWasteStreamSelection";
import { WasteStreamMergeDialog } from "./WasteStreamMerge/WasteStreamMergeDialog";

const WASTE_STREAMS = gql(`
  query WasteStreams(
    $facilityId: ID!,
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
    $startDate: Date
    $endDate: Date
    $view: FacilityWasteView
    $tags: [String!]
  ) {
    facility(id: $facilityId) {
      wasteStreams(search: $search, page: $page, pageSize: $pageSize, sort: $sort, startDate: $startDate, endDate: $endDate, view: $view, tags: $tags) {
        items {
          id
          name
          facilityId
          classification
          federalWasteCodes
          stateWasteCodes
          eManifestProfileNumber
          vendorProfileNumber
          formCode
          sourceCode
          managementMethodCode
          vendorAmount
          eManifestAmount
          effectiveAmount
          vendorAmountAllTime
          eManifestAmountAllTime
          mergeCount
          comment
        }
        count
      }
    }
  }
`);

type WasteStreamTileRow = NonNullable<
  NonNullable<WasteStreamsQuery["facility"]>["wasteStreams"]
>["items"][number];

interface WasteStreamsDataGridProps {
  facilityId: string;
}

export const WasteStreamsDataGrid = ({
  facilityId,
}: WasteStreamsDataGridProps) => {
  const theme = useTheme();
  const { isStaff } = useCurrentUser();
  const { tenantId } = useTenant();
  const { data } = useWasteFacility(facilityId);
  const { selectedRows, setSelectedRows, isRowSelectable, canMergeSelected } =
    useWasteStreamSelection<WasteStreamTileRow>();

  const { selectedId, pushIdToUrl, popIdFromUrl } = useSelectedIdInUrl(
    "wasteStreamId",
    { prefixSegment: "wasteStream" }
  );

  const handleRowClick = (params: GridRowParams<WasteStreamTileRow>) => {
    pushIdToUrl(params.row.id);
  };

  const {
    wasteFacilityState: { deferredFilters },
  } = useWasteFacilityState();
  const { omnisearch } = useOmnisearchInput({ isURLDriven: true });
  const [isMergeDialogOpen, setIsMergeDialogOpen] = useState(false);
  const [, setQueryData] = useState<WasteStreamsQuery | undefined>(undefined);

  const exportFilename = useMemo(
    () =>
      data.facility?.name ? `${data.facility.name}-waste-streams` : undefined,
    [data.facility?.name]
  );

  const columns: OmnisearchGridColDef<WasteStreamTileRow>[] = useMemo(() => {
    return [
      {
        field: "name",
        headerName: "Waste Stream",
        flex: 1,
        minWidth: 100,
        sortable: true,
        headerClassName: "wrapHeader",
        renderCell: (params) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flex: 1,
                minWidth: 0,
              }}
            >
              <Typography
                variant="body2"
                title={params.value}
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                }}
              >
                {params.value}
              </Typography>
              {isStaff && (
                <MergedWasteStreamChip mergeCount={params.row.mergeCount} />
              )}
            </Box>
            <ArrowRightIcon sx={{ flexShrink: 0, fontSize: 16 }} />
          </Box>
        ),
      },
      {
        field: "profileNumber",
        headerName: "Profile Number",
        flex: 0.5,
        sortable: true,
        valueGetter: profileNumberValueGetter,
      },
      {
        field: "federalWasteCodes",
        headerName: "Federal Waste Code",
        flex: 0.5,
        sortable: true,
        valueGetter(_, row) {
          const federalWasteCodes = row.federalWasteCodes || [];
          return federalWasteCodes.join(", ");
        },
        headerClassName: "wrapHeader",
      },
      {
        field: "stateWasteCodes",
        headerName: "State Waste Code",
        flex: 0.5,
        sortable: true,
        valueGetter(_, row) {
          const stateWasteCodes = row.stateWasteCodes || [];
          return stateWasteCodes.join(", ");
        },
        headerClassName: "wrapHeader",
      },
      {
        field: "classification",
        headerName: "Classification",
        flex: 0.5,
        filterable: false,
        sortable: true,
        valueFormatter: (_, row) =>
          prettyPrintWasteClassification(row.classification),
        headerClassName: "wrapHeader",
      },
      {
        field: "formCode",
        headerName: "Form Code",
        flex: 0.3,
        sortable: true,
        headerClassName: "wrapHeader",
      },
      {
        field: "sourceCode",
        headerName: "Source Code",
        flex: 0.3,
        sortable: true,
        headerClassName: "wrapHeader",
      },
      {
        field: "managementMethodCode",
        headerName: "Management Method Code",
        flex: 0.5,
        sortable: true,
        headerClassName: "wrapHeader",
      },
      {
        field: "vendorAmount",
        headerName: "Amount (Vendor)",
        flex: 0.4,
        filterable: false,
        sortable: true,
        headerClassName: "wrapHeader",
        valueFormatter: formatAmountInPounds,
      },
      {
        field: "eManifestAmount",
        headerName: "Amount (e‑Manifest)",
        flex: 0.4,
        filterable: false,
        sortable: true,
        headerClassName: "wrapHeader",
        valueFormatter: formatAmountInPounds,
      },
      {
        field: "effectiveAmount",
        headerName: "Effective Amount",
        flex: 0.4,
        filterable: false,
        sortable: false,
        headerClassName: "wrapHeader",
        valueFormatter: formatAmountInPounds,
        description:
          "Per manifest line: uses e-Manifest amount if available, otherwise vendor amount",
      },
    ];
  }, []);

  return (
    <>
      <OmnisearchDataGrid<
        WasteStreamsQuery,
        WasteStreamsQueryVariables,
        WasteStreamTileRow
      >
        persistenceKey="waste-streams-table"
        isURLDriven={true}
        columns={columns}
        dataQuery={WASTE_STREAMS}
        getItems={(data) => data.facility?.wasteStreams?.items ?? []}
        getCount={(data) => data.facility?.wasteStreams?.count ?? 0}
        dataQueryVariables={{
          facilityId,
          startDate: deferredFilters.startDate,
          endDate: deferredFilters.endDate,
          view: deferredFilters.view,
          tags: deferredFilters.tags,
        }}
        initialPageSize={10}
        withPadding={false}
        defaultSearch=""
        noDataMessage={"No Waste Streams found for this facility."}
        initialSortModel={[{ field: "name", sort: "asc" }]}
        onRowClick={handleRowClick}
        disableRowSelectionOnClick={true}
        onDataStateChange={(data) => {
          setQueryData(data ?? undefined);
        }}
        commandButtons={[
          isStaff ? (
            <Button
              key="merge-waste-streams-button"
              size="small"
              variant="contained"
              sx={{
                color: theme.palette.primary.contrastText,
                backgroundColor: theme.palette.warning.light,
              }}
              endIcon={<ArrowRightIcon />}
              disabled={!canMergeSelected}
              title={`Merge selected waste streams`}
              onClick={() => setIsMergeDialogOpen(true)}
            >
              Merge Waste Streams
            </Button>
          ) : null,
          <WasteStreamExportDropdown
            key="export-waste-streams-button"
            isStaff={isStaff}
            disabled={!facilityId}
            facilityId={facilityId}
            tenantId={tenantId ?? ""}
            fileName={exportFilename}
            filter={{
              search: omnisearch,
              startDate: deferredFilters.startDate,
              endDate: deferredFilters.endDate,
              view: deferredFilters.view,
              tags: deferredFilters.tags,
            }}
            dataQuery={WASTE_STREAMS}
            dataQueryVariables={{
              facilityId,
              startDate: deferredFilters.startDate,
              endDate: deferredFilters.endDate,
              view: deferredFilters.view,
              tags: deferredFilters.tags,
              search: omnisearch || undefined,
            }}
            onWasteStreamClick={pushIdToUrl}
          />,
        ]}
        selectedIds={selectedRows.map((row) => row.id)}
        onSelectedRowsChanged={isStaff ? setSelectedRows : undefined}
        isRowSelectable={isRowSelectable}
        checkboxSelection={isStaff}
        disableSelectAll
        additionalFilterColumns={[
          {
            filterKeyType: "enumAutocomplete",
            key: "classification",
            header: "Classification",
            enumValues: Object.values(WasteClassification),
            enumPresentationFunction: (value) =>
              prettyPrintWasteClassification(value),
          },
        ]}
      />

      {isMergeDialogOpen && (
        <WasteStreamMergeDialog
          onClose={() => {
            setIsMergeDialogOpen(false);
            setSelectedRows([]); // Clear selections when dialog closes
          }}
          selectedRows={selectedRows}
        />
      )}

      {selectedId && (
        <WasteStreamDetailsModal
          open={!!selectedId}
          onClose={() => popIdFromUrl()}
          wasteStreamId={selectedId}
        />
      )}
    </>
  );
};

export function profileNumberValueGetter(_: unknown, row: WasteStreamTileRow) {
  const eManifestProfileNumber = row.eManifestProfileNumber;
  const vendorProfileNumber = row.vendorProfileNumber;

  if (
    eManifestProfileNumber &&
    vendorProfileNumber &&
    vendorProfileNumber !== eManifestProfileNumber
  ) {
    return `Vendor: ${vendorProfileNumber}, e‑Manifest: ${eManifestProfileNumber}`;
  } else if (eManifestProfileNumber) {
    return eManifestProfileNumber;
  } else if (vendorProfileNumber) {
    return vendorProfileNumber;
  } else {
    return "";
  }
}
